import { Controller } from "@hotwired/stimulus"
import { pluralize, metric, dynos } from "../../lib/helpers"
import { reactor } from "../../lib/reactor"

export default class extends Controller {
  static targets = ["instructionsFrame"]

  static values = {
    type: String,
    language: { type: String, default: "ruby" },
    rubyFramework: { type: String, default: "rails" },
    pythonFramework: { type: String, default: "django_wsgi" },
    nodeFramework: { type: String, default: "express" },
    // Class tokens from AutoscalersHelper (instruction_card_*_classes).
    cardRuntimeActiveClass: String,
    cardFrameworkActiveClass: String,
    cardInactiveClass: String,
    name: { type: String, default: "" },
    // Collection worker library (wire key). Bound from the adapter select.
    adapter: { type: String, default: "" },
    // Integration Turbo Frame compose endpoint (path only).
    instructionsUrl: { type: String, default: "" },
    minimum: Number,
    maximum: Number,
    thresholdDown: Number,
    thresholdUp: Number,
    quantityDown: Number,
    quantityUp: Number,
    sensitivityDown: Number,
    sensitivityUp: Number,
    cooldownDown: Number,
    cooldownUp: Number,
    checkupFrequency: { type: Number, default: 15 },
    ratio: Number,
    limitDown: Number,
    limitUp: Number,
  }

  // Field keys a threshold/sensitivity preset button may declare via data-*-param.
  static PRESET_FIELDS = [
    "minimum",
    "thresholdDown",
    "thresholdUp",
    "quantityDown",
    "quantityUp",
    "sensitivityDown",
    "sensitivityUp",
    "cooldownDown",
    "cooldownUp",
  ]

  // Active chip: filled. Named idle: hover + pointer. Custom idle: flat ratio-band row only.
  // Keep active/inactive in sync with ComponentsHelper::RICH_DROPDOWN_*_CHIP.
  static PRESET_ACTIVE_CLASSES =
    "bg-white dark:bg-gray-700 shadow-sm ring-1 ring-gray-300 dark:ring-gray-700 text-gray-900 dark:text-white"
  static PRESET_INACTIVE_CLASSES =
    "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-white dark:hover:bg-gray-700 cursor-pointer"
  static PRESET_CUSTOM_IDLE_CLASSES =
    "text-gray-600 dark:text-gray-400 cursor-default"

  // Match JOB_ADAPTER_LABELS in AutoscalersHelper (wire key → display name).
  static ADAPTER_LABELS = {
    sidekiq: "Sidekiq",
    solid_queue: "Solid Queue",
    good_job: "Good Job",
    que: "Que",
    queue_classic: "Queue Classic",
    delayed_job: "Delayed Job",
    resque: "Resque",
    bunny: "Bunny",
    celery: "Celery",
    rq: "RQ",
    dramatiq: "Dramatiq",
    bullmq: "BullMQ",
    bull: "Bull",
    pg_boss: "pg-boss",
  }

  // Match Instructions::Catalog::ADAPTER_LANGUAGE.
  static ADAPTER_LANGUAGE = {
    sidekiq: "ruby",
    solid_queue: "ruby",
    good_job: "ruby",
    que: "ruby",
    queue_classic: "ruby",
    delayed_job: "ruby",
    resque: "ruby",
    bunny: "ruby",
    celery: "python",
    rq: "python",
    dramatiq: "python",
    bullmq: "node",
    bull: "node",
    pg_boss: "node",
  }

  static DEFAULT_FRAMEWORK = {
    ruby: "rails",
    python: "django_wsgi",
    node: "express",
  }

  // Adapters that reject empty queues (JobPlanConfigurable::QUEUES_REQUIRED_ADAPTERS).
  static QUEUES_REQUIRED_ADAPTERS = new Set(["bunny", "celery", "dramatiq"])

  // Working=Yes adapters only (Autoscaler::HoldWhileWorking::SUPPORTED_ADAPTERS).
  // Conditional libraries and Custom configuration hide the Long-running jobs section.
  static HOLD_WHILE_WORKING_ADAPTERS = new Set([
    "sidekiq",
    "solid_queue",
    "delayed_job",
    "que",
    "good_job",
    "queue_classic",
    "rq",
    "bullmq",
    "bull",
    "pg_boss",
  ])

  connect() {
    this.boundSyncPresetHighlight = this.syncPresetHighlight.bind(this)
    this.instructionsReady = false
    reactor(this)
    // Recompute chip highlight whenever any model field input fires (option 2).
    this.element.addEventListener("input", this.boundSyncPresetHighlight)
    requestAnimationFrame(() => {
      this.boundSyncPresetHighlight()
      this.syncProcessTypeDropdown()
      this.syncAdapterDropdown()
      // Avoid adapter/name valueChanged reloads racing the initial frame src.
      this.instructionsReady = true
    })
  }

  disconnect() {
    this.element.removeEventListener("input", this.boundSyncPresetHighlight)
  }

  morph() {
    this.syncPresetHighlight()
  }

  // Queues only apply when a worker library is set (Custom configuration uses app code).
  adapterSelected() {
    return Boolean(this.adapterValue)
  }

  // Custom configuration under Worker library: app-code escape hatch, not the plan path.
  isCustomConfiguration() {
    return !this.adapterValue
  }

  // Long-running jobs section: only when the selected adapter emits trustworthy wrk.
  holdWhileWorkingSupported() {
    return this.constructor.HOLD_WHILE_WORKING_ADAPTERS.has(this.adapterValue)
  }

  // Plan-path panels: one pre-rendered plan per Worker Library (adapter-accurate boot).
  // Method names match Heroku::AddOn::AutoscalersHelper#instructions_adapter_show_method.
  isPlanSidekiq() {
    return this.adapterValue === "sidekiq"
  }

  isPlanSolidQueue() {
    return this.adapterValue === "solid_queue"
  }

  isPlanGoodJob() {
    return this.adapterValue === "good_job"
  }

  isPlanQue() {
    return this.adapterValue === "que"
  }

  isPlanQueueClassic() {
    return this.adapterValue === "queue_classic"
  }

  isPlanDelayedJob() {
    return this.adapterValue === "delayed_job"
  }

  isPlanResque() {
    return this.adapterValue === "resque"
  }

  isPlanBunny() {
    return this.adapterValue === "bunny"
  }

  isPlanCelery() {
    return this.adapterValue === "celery"
  }

  isPlanRq() {
    return this.adapterValue === "rq"
  }

  isPlanDramatiq() {
    return this.adapterValue === "dramatiq"
  }

  isPlanBullmq() {
    return this.adapterValue === "bullmq"
  }

  isPlanBull() {
    return this.adapterValue === "bull"
  }

  isPlanPgBoss() {
    return this.adapterValue === "pg_boss"
  }

  // Collection queues field help, tailored to the selected worker library (HTML for highlights).
  queuesHelpDescription() {
    const howTo =
      "Type a name and press Enter, Tab, or a comma to add it (spaces are allowed inside a name)."
    const adapter = this.adapterValue || ""
    if (!adapter) return ""

    const name = this.constructor.ADAPTER_LABELS[adapter] || adapter

    if (this.constructor.QUEUES_REQUIRED_ADAPTERS.has(adapter)) {
      return `${name} requires at least one queue name. ${howTo}`
    }

    const emptyHint = `<strong class="font-semibold text-gray-900 dark:text-white">Leave empty to monitor all queues.</strong>`
    return `Queue names this process works on for ${name}. ${howTo} ${emptyHint}`
  }

  selectLanguage({ params: { language }, currentTarget }) {
    this.languageValue = language
    const defaults = this.constructor.DEFAULT_FRAMEWORK
    if (language === "ruby") this.rubyFrameworkValue = defaults.ruby
    if (language === "python") this.pythonFrameworkValue = defaults.python
    if (language === "node") this.nodeFrameworkValue = defaults.node
    if (currentTarget) {
      this.updateCardStyles(
        currentTarget.closest("[data-language-container]"),
        currentTarget,
        "[data-language-tab]",
        this.cardRuntimeActiveClassValue
      )
    }
    this.reloadInstructions()
  }

  selectFramework({ params: { group, value }, currentTarget }) {
    this[`${group}Value`] = value
    if (currentTarget) {
      this.updateCardStyles(
        currentTarget.closest("[data-fw-container]"),
        currentTarget,
        "[data-fw-tab]",
        this.cardFrameworkActiveClassValue
      )
    }
    this.reloadInstructions()
  }

  adapterValueChanged() {
    this.syncLanguageFromAdapter()
    this.syncAdapterDropdown()
    this.reloadInstructions()
  }

  // Collection Worker Library rich dropdown: set wire key (blank = Custom).
  // Prefer data-adapter-option so empty Custom is not lost if Stimulus omits blank params.
  selectAdapter({ params, currentTarget }) {
    let adapter = ""
    if (currentTarget?.hasAttribute("data-adapter-option")) {
      adapter = currentTarget.getAttribute("data-adapter-option") || ""
    } else if (params.adapter != null) {
      adapter = String(params.adapter)
    }
    this.setModelField("adapter", adapter)
  }

  // Process-type rich dropdown (formation cache). Custom key opens free-text.
  selectProcessType({ currentTarget }) {
    const key = currentTarget?.getAttribute("data-process-type-option") || ""
    if (key === "__custom__") {
      this.setModelField("name", "")
      this.syncProcessTypeDropdown()
      const customInput = this.element.querySelector(
        "[data-autoscaler--form-process-type-custom-input]"
      )
      if (customInput) {
        customInput.value = ""
        customInput.focus()
      }
      return
    }
    this.setModelField("name", key)
    this.syncProcessTypeDropdown()
  }

  customProcessTypeInput({ currentTarget }) {
    const value = currentTarget?.value || ""
    this.setModelField("name", value)
    this.syncProcessTypeDropdown()
  }

  // Closed trigger + option visibility for the process-type rich dropdown.
  syncProcessTypeDropdown() {
    const group = this.element.querySelector("[data-autoscaler--form-process-type-group]")
    if (!group) return

    const name = this.nameValue || ""
    const customKey = "__custom__"
    const inactiveClasses = this.constructor.PRESET_INACTIVE_CLASSES.split(/\s+/).filter(Boolean)
    const activeClasses = this.constructor.PRESET_ACTIVE_CLASSES.split(/\s+/).filter(Boolean)

    let matched = false
    group.querySelectorAll("[data-process-type-option]").forEach((option) => {
      const key = option.getAttribute("data-process-type-option") || ""
      if (key === customKey) return
      const isActive = key === name
      if (isActive) matched = true
      option.setAttribute("aria-selected", isActive ? "true" : "false")
      option.hidden = isActive
      option.classList.remove(...activeClasses, ...inactiveClasses)
      option.classList.add(...inactiveClasses)
    })

    const customSelected = !matched
    const customOption = group.querySelector(`[data-process-type-option="${customKey}"]`)
    if (customOption) {
      customOption.setAttribute("aria-selected", customSelected ? "true" : "false")
      customOption.hidden = customSelected
      customOption.classList.remove(...activeClasses, ...inactiveClasses)
      customOption.classList.add(...inactiveClasses)
    }

    const labelEl = group.querySelector("[data-rich-dropdown-trigger-label]")
    const blurbEl = group.querySelector("[data-rich-dropdown-trigger-blurb]")
    let cueKind = "worker"
    if (customSelected) {
      if (labelEl) labelEl.textContent = group.dataset.customLabel || "Custom"
      if (blurbEl) {
        blurbEl.textContent =
          group.dataset.customBlurb ||
          "Type a process name that is not in the formation list yet."
      }
      cueKind =
        customOption?.getAttribute("data-process-type-cue") || "worker"
    } else {
      const option = group.querySelector(
        `[data-process-type-option="${CSS.escape(name)}"]`
      )
      if (option) {
        const label = option.getAttribute("data-rich-dropdown-option-label")
        const blurb = option.getAttribute("data-rich-dropdown-option-blurb")
        if (labelEl && label != null) labelEl.textContent = label
        if (blurbEl && blurb != null) blurbEl.textContent = blurb
        cueKind = option.getAttribute("data-process-type-cue") || "worker"
      }
    }
    this.syncProcessTypeTriggerCue(group, cueKind)

    const customWrap = this.element.querySelector(
      "[data-autoscaler--form-process-type-custom]"
    )
    if (customWrap) customWrap.classList.toggle("hidden", !customSelected)

    const customInput = this.element.querySelector(
      "[data-autoscaler--form-process-type-custom-input]"
    )
    if (customInput && customSelected && customInput.value !== name) {
      customInput.value = name
    }
  }

  // Swap the closed-trigger hex cue (Web vs Worker) from active-tone templates
  // (siblings of the rich-dropdown group, under the form controller element).
  syncProcessTypeTriggerCue(group, cueKind) {
    const kind = cueKind === "web" ? "web" : "worker"
    const source = this.element.querySelector(
      `[data-process-type-cue-source="${kind}"] [data-rich-dropdown-cue]`
    )
    const trigger = group.querySelector(".rich-dropdown-trigger")
    const triggerCue = trigger?.querySelector("[data-rich-dropdown-cue]")
    if (!source || !triggerCue) return
    if (triggerCue.getAttribute("data-process-type-cue") === kind) return
    triggerCue.replaceWith(source.cloneNode(true))
  }

  // Refresh the closed trigger (label, blurb, logo). Hide the selected row from the
  // list so it only appears on the trigger (Strategy dropdown pattern).
  syncAdapterDropdown() {
    const group = this.element.querySelector("[data-autoscaler--form-adapter-group]")
    if (!group) return

    const key = this.adapterValue || ""
    const inactiveClasses = this.constructor.PRESET_INACTIVE_CLASSES.split(/\s+/).filter(Boolean)
    const activeClasses = this.constructor.PRESET_ACTIVE_CLASSES.split(/\s+/).filter(Boolean)

    group.querySelectorAll("[data-adapter-option]").forEach((option) => {
      const isActive = (option.getAttribute("data-adapter-option") || "") === key
      option.setAttribute("aria-selected", isActive ? "true" : "false")
      option.hidden = isActive
      // List rows are never the "filled" active chip; selection is the trigger only.
      option.classList.remove(...activeClasses, ...inactiveClasses)
      option.classList.add(...inactiveClasses)
    })

    const labelEl = group.querySelector("[data-rich-dropdown-trigger-label]")
    const blurbEl = group.querySelector("[data-rich-dropdown-trigger-blurb]")
    const logoEl = group.querySelector("[data-rich-dropdown-trigger-logo]")
    const option = group.querySelector(`[data-adapter-option="${CSS.escape(key)}"]`)

    if (!option) return

    const label = option.getAttribute("data-rich-dropdown-option-label")
    const blurb = option.getAttribute("data-rich-dropdown-option-blurb")
    if (labelEl && label != null) labelEl.textContent = label
    if (blurbEl && blurb != null) blurbEl.textContent = blurb

    if (logoEl) {
      const src = option.getAttribute("data-rich-dropdown-option-logo")
      if (src) logoEl.setAttribute("src", src)
      const invert = option.getAttribute("data-rich-dropdown-option-invert") === "true"
      logoEl.classList.toggle("dark:brightness-0", invert)
      logoEl.classList.toggle("dark:invert", invert)
    }
  }

  nameValueChanged() {
    this.syncProcessTypeDropdown()
    this.reloadInstructions()
  }

  syncLanguageFromAdapter() {
    const adapter = this.adapterValue
    if (!adapter) return

    const lang = this.constructor.ADAPTER_LANGUAGE[adapter]
    if (!lang) return

    this.languageValue = lang
    if (adapter === "solid_queue") {
      this.rubyFrameworkValue = "rails"
      return
    }

    const defaults = this.constructor.DEFAULT_FRAMEWORK
    if (lang === "ruby") this.rubyFrameworkValue = defaults.ruby
    if (lang === "python") this.pythonFrameworkValue = defaults.python
    if (lang === "node") this.nodeFrameworkValue = defaults.node
  }

  strategyKey() {
    const t = this.typeValue || ""
    if (t.includes("RequestQueueTime")) return "rqt"
    if (t.includes("JobQueueLatency") || t.includes("JobLatency")) return "jql"
    if (t.includes("JobQueueSize") || t.includes("JobQueue")) return "jqs"
    if (t.includes("CPUActivity")) return "cpu"
    return "rqt"
  }

  currentFramework() {
    const lang = this.languageValue
    if (lang === "ruby") return this.rubyFrameworkValue
    if (lang === "python") return this.pythonFrameworkValue
    return this.nodeFrameworkValue
  }

  reloadInstructions() {
    if (!this.instructionsReady) return
    if (!this.hasInstructionsFrameTarget) return
    const base = this.instructionsUrlValue
    if (!base) return

    const url = new URL(base, window.location.origin)
    url.searchParams.set("strategy", this.strategyKey())
    url.searchParams.set("language", this.languageValue)
    url.searchParams.set("framework", this.currentFramework())
    if (this.adapterValue) {
      url.searchParams.set("adapter", this.adapterValue)
    } else {
      url.searchParams.delete("adapter")
    }
    const name = (this.nameValue || "").trim()
    if (name) url.searchParams.set("name", name)
    else url.searchParams.delete("name")

    this.instructionsFrameTarget.src = url.pathname + url.search
  }

  updateCardStyles(container, activeTab, selector, activeClassString) {
    const active = activeClassString.split(/\s+/).filter(Boolean)
    const inactive = this.cardInactiveClassValue.split(/\s+/).filter(Boolean)
    container.querySelectorAll(selector).forEach(tab => {
      if (tab === activeTab) {
        tab.classList.remove(...inactive)
        tab.classList.add(...active)
      } else {
        tab.classList.remove(...active)
        tab.classList.add(...inactive)
      }
    })
  }

  isRuby() {
    return this.languageValue === "ruby"
  }

  isPython() {
    return this.languageValue === "python"
  }

  isNode() {
    return this.languageValue === "node"
  }

  instanceName() {
    const name = this.nameValue.trim()
    return name ? `:${name}` : ":worker"
  }

  instanceNameRaw() {
    const name = this.nameValue.trim()
    return name ? name : "worker"
  }

  // RQT framework show conditions (one pre-rendered plan per framework).
  isRubyRails() {
    return this.rubyFrameworkValue === "rails"
  }

  isRubySinatra() {
    return this.rubyFrameworkValue === "sinatra"
  }

  isRubyHanami() {
    return this.rubyFrameworkValue === "hanami"
  }

  isRubyRack() {
    return this.rubyFrameworkValue === "rack"
  }

  isPythonDjangoWsgi() {
    return this.pythonFrameworkValue === "django_wsgi"
  }

  isPythonDjangoAsgi() {
    return this.pythonFrameworkValue === "django_asgi"
  }

  isPythonFlask() {
    return this.pythonFrameworkValue === "flask"
  }

  isPythonQuart() {
    return this.pythonFrameworkValue === "quart"
  }

  isPythonFastapi() {
    return this.pythonFrameworkValue === "fastapi"
  }

  isPythonStarlette() {
    return this.pythonFrameworkValue === "starlette"
  }

  isNodeExpress() {
    return this.nodeFrameworkValue === "express"
  }

  isNodeConnect() {
    return this.nodeFrameworkValue === "connect"
  }

  isNodeKoa() {
    return this.nodeFrameworkValue === "koa"
  }

  isNodeFastify() {
    return this.nodeFrameworkValue === "fastify"
  }

  isNodeSails() {
    return this.nodeFrameworkValue === "sails"
  }

  isNodeNest() {
    return this.nodeFrameworkValue === "nest"
  }

  isNodeNext() {
    return this.nodeFrameworkValue === "next"
  }

  minimumDescription() {
    if (this.minimumValue === 0)
      return `Can scale down to ${dynos("0 dynos")}.`

    return `Won't scale below ${dynos(pluralize(this.minimumValue, "{{n}} dyno", "{{n}} dynos"))}.`
  }

  maximumDescription() {
    return `Won't scale above ${dynos(pluralize(this.maximumValue, "{{n}} dyno", "{{n}} dynos"))}.`
  }

  thresholdDownDescription() {
    if (this.typeValue === "Autoscaler::RequestQueueTimeStrategy")
      return `Scale down when queue time drops below ${metric(`${this.thresholdDownValue} ms`)}.`

    if (this.typeValue === "Autoscaler::CPUActivityStrategy")
      return `Scale down when CPU drops below ${metric(`${this.thresholdDownValue}%`)}.`

    // JobQueueLatency: client macros report oldest-job wait in seconds.
    return `Scale down when latency drops below ${metric(`${this.thresholdDownValue} seconds`)}.`
  }

  thresholdUpDescription() {
    if (this.typeValue === "Autoscaler::RequestQueueTimeStrategy")
      return `Scale up when queue time exceeds ${metric(`${this.thresholdUpValue} ms`)}.`

    if (this.typeValue === "Autoscaler::CPUActivityStrategy")
      return `Scale up when CPU exceeds ${metric(`${this.thresholdUpValue}%`)}.`

    // JobQueueLatency: client macros report oldest-job wait in seconds.
    return `Scale up when latency exceeds ${metric(`${this.thresholdUpValue} seconds`)}.`
  }

  quantityDownDescription() {
    return `Remove ${dynos(pluralize(this.quantityDownValue, "{{n}} dyno", "{{n}} dynos"))} per step.`
  }

  quantityUpDescription() {
    return `Add ${dynos(pluralize(this.quantityUpValue, "{{n}} dyno", "{{n}} dynos"))} per step.`
  }

  ratioDescription() {
    if (this.ratioValue === 1)
      return `${dynos("1 dyno")} targets about ${metric("1 job")} of backlog. Not the same as concurrency.`

    return `${dynos("1 dyno")} targets about ${metric(pluralize(this.ratioValue, "{{n}} job", "{{n}} jobs"))} of backlog. Not the same as concurrency.`
  }

  // Soft threshold/sensitivity preset chips (RQT / JQL / CPU). Loads numbers only.
  applyPreset({ params }) {
    if (!params.key || params.key === "custom") return

    this.constructor.PRESET_FIELDS.forEach((field) => {
      const raw = params[field]
      if (raw === undefined || raw === null || raw === "") return
      this.setModelField(field, Number(raw))
    })
    // input events from setModelField already call syncPresetHighlight.
  }

  // Soft ratio starter-estimate chips (RPM / JQS). Loads ratio only.
  applyRatioBand({ params }) {
    if (!params.key || params.key === "custom") return
    if (params.ratio === undefined || params.ratio === null || params.ratio === "") return

    this.setModelField("ratio", Number(params.ratio))
  }

  setModelField(field, value) {
    this[`${field}Value`] = value

    const input = this.element.querySelector(`[data-autoscaler--form-model="${field}"]`)
    if (!input) return

    const next = String(value)
    if (input.value !== next) input.value = next
    input.dispatchEvent(new Event("input", { bubbles: true }))
  }

  // Highlight means "current fields exactly match this chip". Else Custom.
  // Rich-dropdown presets: also refresh the closed trigger label/blurb.
  syncPresetHighlight() {
    const presetGroup = this.element.querySelector("[data-autoscaler--form-preset-group]")
    if (presetGroup) {
      const key = this.matchedPresetKey(presetGroup, "data-autoscaler--form-preset-key")
      this.highlightChoiceGroupByKey(presetGroup, "[data-autoscaler--form-preset-key]", key)
      this.syncRichDropdownTrigger(presetGroup, key, "data-autoscaler--form-preset-key")
      return
    }

    const bandGroup = this.element.querySelector("[data-autoscaler--form-ratio-band-group]")
    if (bandGroup) {
      const key = this.matchedRatioBandKey(bandGroup)
      this.highlightChoiceGroupByKey(bandGroup, "[data-autoscaler--form-ratio-band-key]", key)
      this.syncRichDropdownTrigger(bandGroup, key, "data-autoscaler--form-ratio-band-key")
    }
  }

  // Copy matched option (or Custom) copy into the rich-dropdown trigger spans.
  syncRichDropdownTrigger(group, activeKey, keyAttribute) {
    const labelEl = group.querySelector("[data-rich-dropdown-trigger-label]")
    const blurbEl = group.querySelector("[data-rich-dropdown-trigger-blurb]")
    if (!labelEl || !blurbEl) return

    if (activeKey === "custom") {
      labelEl.textContent = group.dataset.customLabel || "Custom"
      blurbEl.textContent =
        group.dataset.customBlurb ||
        "Your settings do not match a starting profile. Edit freely or pick a profile above."
      return
    }

    const option = group.querySelector(`[${keyAttribute}="${activeKey}"]`)
    if (!option) return
    const label = option.getAttribute("data-rich-dropdown-option-label")
    const blurb = option.getAttribute("data-rich-dropdown-option-blurb")
    if (label != null) labelEl.textContent = label
    if (blurb != null) blurbEl.textContent = blurb
  }

  matchedPresetKey(group, keyAttribute) {
    const chips = group.querySelectorAll(`[${keyAttribute}]`)
    for (const chip of chips) {
      const key = chip.getAttribute(keyAttribute)
      if (!key || key === "custom") continue
      if (this.chipMatchesCurrentFields(chip)) return key
    }
    return "custom"
  }

  matchedRatioBandKey(group) {
    const chips = group.querySelectorAll("[data-autoscaler--form-ratio-band-key]")
    for (const chip of chips) {
      const key = chip.getAttribute("data-autoscaler--form-ratio-band-key")
      if (!key || key === "custom") continue
      const ratio = chip.getAttribute("data-autoscaler--form-ratio-param")
      if (ratio === null) continue
      if (Number(ratio) === Number(this.ratioValue)) return key
    }
    return "custom"
  }

  chipMatchesCurrentFields(chip) {
    return this.constructor.PRESET_FIELDS.every((field) => {
      const attr = `data-autoscaler--form-${this.dasherize(field)}-param`
      const raw = chip.getAttribute(attr)
      if (raw === null) return true // field not part of this preset
      return Number(raw) === Number(this[`${field}Value`])
    })
  }

  dasherize(camel) {
    return camel.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)
  }

  // List rows are never the filled "active" chip: selection is the trigger only
  // (Strategy / process-type / adapter pattern). Hide the matched option from the
  // list so it does not appear twice. Custom has no list row.
  highlightChoiceGroupByKey(group, selector, activeKey) {
    const active = this.constructor.PRESET_ACTIVE_CLASSES.split(/\s+/).filter(Boolean)
    const inactive = this.constructor.PRESET_INACTIVE_CLASSES.split(/\s+/).filter(Boolean)
    const customIdle = this.constructor.PRESET_CUSTOM_IDLE_CLASSES.split(/\s+/).filter(Boolean)
    const keyAttribute = selector.includes("ratio-band")
      ? "data-autoscaler--form-ratio-band-key"
      : "data-autoscaler--form-preset-key"

    group.querySelectorAll(selector).forEach((button) => {
      const key = button.getAttribute(keyAttribute)
      button.classList.remove(...active, ...inactive, ...customIdle)
      // Custom is reverse-matched on the rich-dropdown trigger only (no list row).
      if (key === "custom") return

      const isActive = key === activeKey
      button.classList.add(...inactive)
      button.hidden = isActive

      if (button.getAttribute("role") === "option") {
        button.setAttribute("aria-selected", isActive ? "true" : "false")
      }
    })
  }

  limitDownDescription() {
    if (this.limitDownValue === 0)
      return `No limit.`

    return `Max decrease of ${dynos(pluralize(this.limitDownValue, "{{n}} dyno", "{{n}} dynos"))} per step.`
  }

  limitUpDescription() {
    if (this.limitUpValue === 0)
      return `No limit.`

    return `Max increase of ${dynos(pluralize(this.limitUpValue, "{{n}} dyno", "{{n}} dynos"))} per step.`
  }

  sensitivityDownDescription() {
    const lag = this.sensitivityLagPhrase(this.sensitivityDownValue)
    if (this.sensitivityDownValue === 1)
      return `Scale down ${metric("immediately")} when a checkup calls for it${lag}.`

    return `Scale down after ${metric(pluralize(this.sensitivityDownValue, "{{n}} consecutive checkup", "{{n}} consecutive checkups"))} call for it${lag}.`
  }

  sensitivityUpDescription() {
    const lag = this.sensitivityLagPhrase(this.sensitivityUpValue)
    if (this.sensitivityUpValue === 1)
      return `Scale up ${metric("immediately")} when a checkup calls for it${lag}.`

    return `Scale up after ${metric(pluralize(this.sensitivityUpValue, "{{n}} consecutive checkup", "{{n}} consecutive checkups"))} call for it${lag}.`
  }

  cooldownDownDescription() {
    if (!this.cooldownDownValue || this.cooldownDownValue === 0)
      return `${metric("No downscale cooldown")}. Scale down again as soon as sensitivity is met.`

    return `After a downscale, wait ${metric(this.formatDuration(this.cooldownDownValue))} before another downscale.`
  }

  cooldownUpDescription() {
    if (!this.cooldownUpValue || this.cooldownUpValue === 0)
      return `${metric("No upscale cooldown")}. Scale up again as soon as sensitivity is met (can stack while dynos boot).`

    return `After an upscale, wait ${metric(this.formatDuration(this.cooldownUpValue))} before another upscale so new capacity can boot.`
  }

  sensitivityLagPhrase(count) {
    const frequency = this.checkupFrequencyValue
    if (!count || !frequency) return ""

    const seconds = count * frequency
    return ` (~${this.formatDuration(seconds)} at this app's ${frequency}s checkup frequency)`
  }

  formatDuration(totalSeconds) {
    const seconds = Math.max(0, Math.round(Number(totalSeconds) || 0))
    if (seconds < 60) return `${seconds}s`

    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const rem = seconds % 60
    const parts = []
    if (hours > 0) parts.push(`${hours}h`)
    if (minutes > 0) parts.push(`${minutes}m`)
    if (rem > 0) parts.push(`${rem}s`)
    return parts.join(" ")
  }
}
